import { uniqueId } from 'lodash';

import { createMockEnvironmentProduction } from 'dashboard/lib/stubs/environment';
import { createMockJSONField } from 'dashboard/lib/stubs/fields';
import {
  InputSchemaType,
  TriggerAccount,
  TriggerAccountAttributes,
  TriggerApi,
  TriggerCase,
  TriggerCaseAttributes,
  TriggerComputedField,
  TriggerComputedFieldAttributes,
  TriggerDirect,
  TriggerEvent,
  TriggerEventAttributes,
  TriggerRecurring,
  TriggerRecurringAttributes,
  TriggerRelationships,
  TriggerSchedule,
  TriggerScheduleAttributes,
} from 'dashboard/models/trigger';
import { EventName } from 'lib/constants/events';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchema } from 'lib/json-schema';

export const generateTriggerId = () => uniqueId('trge_');
export const generateTriggerApiId = () => uniqueId('trga_');
export const generateTriggerCaseId = () => uniqueId('trgc_');
export const generateTriggerDirectId = () => uniqueId('trgd_');
export const generateTriggerAccountId = () => uniqueId('trgacc_');

const mockEnvironment = createMockEnvironmentProduction();

export function createMockTrigger(
  attributes?: Partial<TriggerEventAttributes>,
  relationships?: Partial<TriggerRelationships>,
  id?: string,
): TriggerEvent {
  return {
    attributes: {
      'event-name': EventName.InquiryCompleted,
      rules: {
        or: [{ and: [] }],
      },
      'template-filters': {},
      ...attributes,
    },
    id: id || generateTriggerId(),
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerEvent,
  };
}

export function createMockComputedFieldTrigger(
  attributes?: Partial<TriggerComputedFieldAttributes>,
  id?: string,
): TriggerComputedField {
  return {
    id: id || generateTriggerId(),
    type: ApiModel.TriggerComputedField,
    relationships: {
      environment: { data: mockEnvironment },
    },
    attributes: {
      'field-schema-object-token': 'txntp_123',
      'field-key': 'some-field',
      config: [EventName.TransactionCreated],
      ...attributes,
    },
  };
}

export function createMockRecurringTrigger(
  attributes?: Partial<TriggerRecurringAttributes>,
  relationships?: Partial<TriggerRelationships>,
  id?: string,
): TriggerRecurring {
  return {
    attributes: {
      'trigger-object-type': ApiModel.Inquiry,
      rrule: 'FREQ=WEEKLY;',
      'start-at': '2022/12/12',
      filter: [
        {
          name: 'id',
          operator: '=',
          value: '123',
          unit: '',
        },
      ],
      ...attributes,
    },
    id: id || generateTriggerId(),
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerRecurring,
  };
}

export function createMockScheduleTrigger(
  attributes?: Partial<TriggerScheduleAttributes>,
  relationships?: Partial<TriggerRelationships>,
  id?: string,
): TriggerSchedule {
  return {
    attributes: {
      'trigger-object-type': ApiModel.Inquiry,
      ...attributes,
    },
    id: id || generateTriggerId(),
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerSchedule,
  };
}

export function createMockApiTrigger(
  jsonSchema?: JsonSchema,
  relationships?: Partial<TriggerRelationships>,
  id?: string,
): TriggerApi {
  return {
    id: id || generateTriggerApiId(),
    attributes: {
      'param-schema': [createMockJSONField(jsonSchema)],
    },
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerApi,
  };
}

export function createMockCaseTrigger(
  attributes?: Partial<TriggerCaseAttributes>,
  relationships?: Partial<TriggerRelationships>,
  id?: string,
): TriggerCase {
  return {
    id: id || generateTriggerCaseId(),
    attributes: {
      'param-schema': [
        {
          name: 'name',
          label: 'Name',
          prefill: '',
          placeholder: 'Name',
          type: InputSchemaType.Input,
          required_to_show: [],
        },
      ],
      ...attributes,
    },
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerCase,
  };
}

export function createMockDirectTrigger(
  id?: string,
  relationships?: Partial<TriggerRelationships>,
): TriggerDirect {
  return {
    id: id || generateTriggerDirectId(),
    attributes: {},
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerDirect,
  };
}

export function createMockAccountTrigger(
  attributes?: Partial<TriggerAccountAttributes>,
  relationships?: Partial<TriggerRelationships>,
  id?: string,
): TriggerAccount {
  return {
    id: id || generateTriggerAccountId(),
    attributes: {
      'input-modal-configuration': [],
      ...attributes,
    },
    relationships: {
      environment: { data: mockEnvironment },
      ...relationships,
    },
    type: ApiModel.TriggerAccount,
  };
}
